import { enHubspot } from "./enHubspot";
import { enMsdynamics } from "./enMsdynamics";
import { enPipedrive } from "./enPipedrive";
import { enSalesforce } from "./enSalesforce";
import { enZohoAiConfig } from "./enZohoAiConfig";

/**
 * The `company.<provider>` settings-tree copy, one key per CRM integration.
 *
 * Extracted out of `en.ts` so that adding a CRM adds **zero** lines to that file. `en.ts`
 * is over the 500-line ratchet, which is shrink-only, so an inline provider key would make
 * every future integration pay for an unrelated extraction before it can add a string.
 */
export const enCrmProviders = {
	hubspot: enHubspot,
	zoho: {
		title: "Zoho CRM Integration",
		connected: "Connected",
		notConnected: "Not Connected",
		connect: "Connect",
		disconnect: "Disconnect",
		configureFieldMapping: "Configure Field Mapping",
		fieldMapping: {
			title: "Zoho CRM Field Mapping",
			leads: "Leads",
			contacts: "Contacts",
			accounts: "Accounts",
			deals: "Deals",
			ourField: "Signalist Field",
			zohoField: "Zoho Field",
			active: "Active",
			actions: "Actions",
			addMapping: "Add Mapping",
			saveAll: "Save All",
			selectField: "Select a field",
			noMappings: "No field mappings configured yet.",
			deleteConfirm: "Are you sure you want to delete this mapping?",
			sourceTypeFromRecord: "From record",
			sourceTypeConstant: "Constant value",
			sourceTypeAi: "AI-generated",
			constantPlaceholder: "Enter a literal value",
			constantLabel: 'Constant: "{{value}}"',
			aiBadge: "AI",
			aiConfigure: "Configure",
			aiTargetPlaceholder: "Pick a Zoho field →",
			aiCreditTooltip: "0.1 credits per lead synced — flat, covers all AI fields",
			aiHintTitle: "AI-enriched fields",
			aiHintBody:
				"Let an AI agent classify, research, or extract a value before syncing. AI enrichment costs 0.1 credits per lead — flat, regardless of how many AI fields you add. The field name will match your selected Zoho field.",
			aiSaveDisabledTooltip: "Configure AI to save",
			cannotMap: "— Cannot map —",
			cannotMapTooltip: "Zoho calculated field — Signalist cannot write to this",
			searchRefused:
				"Zoho can't search this field, so Signalist can't find people already in Zoho by their LinkedIn profile. Make it a single-line text field in Zoho, or map the LinkedIn URL onto one that is.",
			preview: {
				button: "Preview with a lead",
				title: "Preview field mapping",
				subtitle:
					"Pick a lead and see exactly which values a sync would write to the CRM. Nothing is written — this is a dry run. AI-enriched fields are not executed here; use their test panel instead.",
				cancel: "Cancel",
				createsLabel: "A sync will create",
				createsByEntity: {
					LEAD: "A sync creates or updates a Lead in the CRM.",
					CONTACT: "A sync creates or updates a Contact (and its Account) in the CRM.",
					ACCOUNT: "A sync creates or updates an Account (and its Contact) in the CRM.",
					DEAL: "A sync creates a Deal in the CRM.",
				},
				prospectLabel: "Lead",
				prospectPlaceholder: "Search leads by name…",
				run: "Run preview",
				value: "Value",
				emptyValue: "Empty for this lead",
				aiNotRun: "AI — not run in preview",
				inactive: "Inactive",
				fallbacksTitle: "Added automatically",
				fallbacksHint:
					"The CRM requires these fields; the sync fills them when no active mapping provides a value.",
				noMappings: "No mappings to preview for this tab.",
				error: "Preview failed. Please try again.",
				warnings: {
					notAllowedValue:
						"Not one of the allowed values for this dropdown field — the CRM will reject it",
					numberExpected: "The CRM expects a number for this field",
					booleanExpected: "The CRM expects true/false for this field",
					readOnlyTarget: "Read-only field in the CRM — the value will be rejected or ignored",
					missingRequiredTitle: "Missing required fields",
					missingRequiredHint:
						"The CRM requires these fields, but no active mapping, AI field, or automatic fallback supplies a value for this lead. A sync would fail:",
				},
				sync: {
					button: "Sync this lead now",
					warning:
						"End-to-end check: runs a real sync for this lead and shows the CRM's response. This creates or updates a real record (AI fields run and are charged as usual).",
					successTitle: "The CRM accepted the record — your mapping works.",
					failedTitle: "The CRM rejected the record:",
					openRecord: "Open in CRM",
					leadRecord: "Lead {{id}}",
					contactRecord: "Contact {{id}}",
					accountRecord: "Account {{id}}",
					dealRecord: "Deal {{id}}",
				},
			},
			metaBadge: {
				required: "Required",
				multi: "Multi",
				dropdown: "Dropdown",
				number: "Number",
				calculated: "Calculated",
				readOnly: "Read-only",
			},
			footerSummary: "{{mapped}} fields mapped · {{ai}} AI-enriched",
			footerCreditPill: "+0.1 credits per lead with AI enrichment",
			aiConfigTitle: "Configure AI-enriched field",
			aiConfigSubtitle: "Target: {{label}}",
			aiConfigPlaceholder:
				"Prompt editor, allowed values, behavior options and test panel will land in the next iteration.",
			aiConfig: enZohoAiConfig,
		},
		tabs: {
			connection: "Connection",
			settings: "Settings",
			dealConfiguration: "Deal Configuration",
			userMapping: "User Mapping",
			syncLogs: "Sync Logs",
		},
		syncTrigger: {
			title: "Sync Prospects to Zoho CRM",
			description: "Choose when prospects should be automatically synchronized to Zoho CRM.",
			DISABLED: "Disabled",
			ON_CAMPAIGN_START: "When campaign starts",
			ON_REPLY: "When prospect replies",
			INHERIT: "Use company default",
			inheritedFrom: "Currently using company setting",
		},
		dealCreationTrigger: {
			title: "Automatic Deal Creation",
			description:
				"Choose when deals should be automatically created in Zoho CRM for leads that already exist.",
			DISABLED: "Off",
			ON_RESPONSE: "On Response",
			ON_POSITIVE_RESPONSE: "On Positive Response",
		},
		dealConfig: {
			title: "Deal Defaults",
			description: "Configure default values for new Zoho CRM deals.",
			defaultPipeline: "Default Pipeline",
			defaultStage: "Default Stage",
			dealNameTemplate: "Deal Name Template",
			dealNameTemplateHelp: "Use {firstName}, {lastName}, {company} as placeholders.",
			closeDateOffsetDays: "Close Date Offset (days)",
			closeDateOffsetHelp: "Number of days from today for the default close date.",
			defaultOwner: "Default Deal Owner",
			// The order is pickZohoDealOwnerId's in the API (PREC-4351).
			defaultOwnerHelp:
				"Owns a deal when no owner was picked and neither the sender's user mapping nor the Zoho account's owner names one. It also pre-fills the owner when you create a deal by hand.",
			autoCreateCampaigns: "Auto-create Zoho Campaigns",
			autoCreateCampaignsHelp:
				"When enabled, a Zoho Campaign is automatically created for each Signalist campaign and linked prospects are added as campaign members.",
			selectPipeline: "Select a pipeline",
			selectStage: "Select a stage",
			selectOwner: "Select an owner",
			saved: "Deal configuration saved successfully.",
			saveError: "Failed to save deal configuration.",
		},
		userMapping: {
			title: "User Mapping",
			description: "Map Signalist users to Zoho CRM owners for deal assignment.",
			signalistUser: "Signalist User",
			zohoOwner: "Zoho Owner",
			selectOwner: "Select an owner",
			noUsers: "No sending users found.",
			saved: "User mappings saved successfully.",
			saveError: "Failed to save user mappings.",
		},
		createDeal: {
			title: "Create Zoho Deal",
			buttonLabel: "Create Deal",
			dealName: "Deal Name",
			pipeline: "Pipeline",
			stage: "Deal Stage",
			owner: "Deal Owner",
			closeDate: "Close Date",
			selectPipeline: "Select a pipeline",
			selectStage: "Select a stage",
			selectOwner: "Select an owner",
			success: "Deal created successfully in Zoho CRM.",
			error: "Failed to create deal in Zoho CRM.",
			missingConfig: "Please configure deal defaults in Zoho CRM settings first.",
		},
		conversationSync: {
			title: "Conversation Sync",
			label: "Sync conversations to Zoho CRM",
			description:
				"When enabled, messages sent to and received from prospects are automatically synced as notes in Zoho CRM.",
			success: "Conversation sync setting updated successfully.",
			error: "Failed to update conversation sync setting.",
		},
		oauth: {
			connectWithZoho: "Connect with Zoho CRM",
			connectedViaOAuth: "Connected via OAuth",
			zohoOrgId: "Organization",
			tokenRefresh: "Token refreshes automatically",
			disconnectTitle: "Disconnect Zoho CRM?",
			disconnectBody: "This will remove the OAuth connection. Zoho CRM syncing will stop.",
			connectError: "Failed to generate authorization URL",
			alreadyDisconnected: "Zoho CRM is already disconnected.",
			disconnectError: "Failed to disconnect from Zoho CRM.",
			// PREC-4353: the stored sign-in was refused; the flag clears on the next sign-in.
			reconnectRequired: "Reconnect required",
			reauthDescription:
				"Zoho CRM no longer accepts the stored sign-in, so nothing syncs. Reconnect to sign in again; your settings and field mappings are kept.",
			reconnect: "Reconnect",
		},
		syncLogs: {
			description: "View the history of data synchronization between Signalist and Zoho CRM.",
			filterByEntity: "Filter by Entity",
			allEntities: "All Entities",
			entityType: "Entity Type",
			status: "Status",
			synced: "Synced",
			failed: "Failed",
			error: "Error",
			startedAt: "Started At",
			completedAt: "Completed At",
			noLogs: "No sync logs found.",
		},
		campaignLinks: {
			title: "Campaign Link",
			description:
				"Link this list to a Zoho Campaign. Synced prospects will be added as campaign members.",
			selectCampaign: "Select a campaign",
			linked: "Linked to campaign",
			unlink: "Unlink",
			link: "Link",
			linkSuccess: "List linked to Zoho Campaign successfully.",
			unlinkSuccess: "List unlinked from Zoho Campaign.",
			linkError: "Failed to link list to campaign.",
			unlinkError: "Failed to unlink list from campaign.",
		},
		resync: {
			title: "Resync with Zoho CRM",
			description:
				"Re-push every already-synced prospect to Zoho CRM using your current field mapping. Useful after you add or change mapped fields.",
			button: "Resync now",
			confirmTitle: "Resync with Zoho CRM?",
			confirmBody:
				"This will re-push every prospect that is already synced to Zoho CRM using your current field mapping. Continue?",
			confirmCta: "Resync",
			success: "Re-syncing {{count}} contact(s) to Zoho. Check the sync log for progress.",
			empty: "No already-synced prospects found.",
			error: "Failed to queue resync. Please try again.",
		},
	},
	pipedrive: enPipedrive,
	msdynamics: enMsdynamics,
	salesforce: enSalesforce,
};
